/**
 * Convert company catalog products array into Ant Design Cascader options
 * Supports both parent products and nested sub-products
 */
export function buildProductCascaderOptions(products = []) {
  if (!Array.isArray(products) || products.length === 0) return [];

  return products.map((p) => {
    const parentPrice = Number(p.price || p.minPrice || 0);
    const parentPriceLabel = parentPrice > 0 ? ` (₹${parentPrice.toLocaleString()})` : "";
    const parentKey = `p:${p.name}`;

    const option = {
      value: parentKey,
      label: `${p.name}${parentPriceLabel}`,
      title: `${p.name}${parentPriceLabel}`,
      raw: p,
      name: p.name,
      displayName: p.name,
      sacHsn: p.sacHsn || "",
      price: parentPrice,
      minPrice: p.minPrice || 0,
      maxPrice: p.maxPrice || 0,
      isSubProduct: false,
    };

    if (Array.isArray(p.subProducts) && p.subProducts.length > 0) {
      option.children = p.subProducts.map((sp) => {
        const subPrice = Number(sp.price || sp.minPrice || 0);
        const subPriceLabel = subPrice > 0 ? ` (₹${subPrice.toLocaleString()})` : "";
        const subKey = `sp:${sp.id || sp.name}`;

        return {
          value: subKey,
          label: `${sp.name}${subPriceLabel}`,
          title: `${sp.name}${subPriceLabel}`,
          raw: sp,
          parentRaw: p,
          name: `${p.name} - ${sp.name}`,
          parentName: p.name,
          subProductName: sp.name,
          displayName: `${p.name} - ${sp.name}`,
          sacHsn: sp.sacHsn || p.sacHsn || "",
          price: subPrice,
          minPrice: sp.minPrice || 0,
          maxPrice: sp.maxPrice || 0,
          description: sp.description || "",
          isSubProduct: true,
        };
      });
    }

    return option;
  });
}

function findOptionDeep(options = [], targetVal) {
  for (const opt of options) {
    if (String(opt.value) === String(targetVal) || String(opt.name) === String(targetVal)) {
      return opt;
    }
    if (Array.isArray(opt.children)) {
      const found = findOptionDeep(opt.children, targetVal);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Recursively find an option in the cascader options tree by value path or single key
 */
export function findCascaderOptionByValue(options = [], valuePath = []) {
  if (!Array.isArray(options) || options.length === 0) return null;
  const path = Array.isArray(valuePath) ? valuePath : [valuePath];
  if (path.length === 0) return null;

  let currentOptions = options;
  let targetOption = null;

  for (let i = 0; i < path.length; i++) {
    const val = path[i];
    const match = currentOptions.find((opt) => String(opt.value) === String(val) || String(opt.name) === String(val));
    if (!match) {
      const deepMatch = findOptionDeep(options, val);
      if (deepMatch) return deepMatch;
      return targetOption;
    }
    targetOption = match;
    if (match.children && Array.isArray(match.children)) {
      currentOptions = match.children;
    }
  }

  return targetOption;
}

/**
 * Resolve a product object from Cascader value and selectedOptions safely
 */
export function resolveCascaderProduct(options = [], value = [], selectedOptions = []) {
  let matchedOption = null;

  if (Array.isArray(value) && value.length > 0) {
    matchedOption = findCascaderOptionByValue(options, value);
  }

  if (!matchedOption && Array.isArray(selectedOptions) && selectedOptions.length > 0) {
    matchedOption = selectedOptions[selectedOptions.length - 1];
  }

  if (!matchedOption && value && !Array.isArray(value)) {
    matchedOption = findCascaderOptionByValue(options, [value]);
  }

  if (!matchedOption) return null;

  let name = matchedOption.name || matchedOption.displayName;
  if (!name && typeof matchedOption.label === "string") {
    name = matchedOption.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
  }
  if (!name) {
    name = "Product Item";
  }

  const displayName = matchedOption.displayName || name;

  const price = Number(
    matchedOption.price !== undefined && matchedOption.price !== null
      ? matchedOption.price
      : matchedOption.minPrice || 0
  );

  const sacHsn = matchedOption.sacHsn || (matchedOption.raw && matchedOption.raw.sacHsn) || "998313";

  return {
    ...matchedOption,
    name,
    displayName,
    price,
    sacHsn,
  };
}

/**
 * Convert products from main company + collaborated companies into Ant Design Cascader options
 * @param {Array} mainProducts - Products of the current / host company
 * @param {Array} collaboratedGroups - Array of { companyCode, companyName, products }
 * @returns {Array} Combined Cascader options tree
 */
export function buildMultiCompanyProductCascaderOptions(mainProducts = [], collaboratedGroups = []) {
  const mainOptions = buildProductCascaderOptions(mainProducts);

  if (!Array.isArray(collaboratedGroups) || collaboratedGroups.length === 0) {
    return mainOptions;
  }

  const validCollabGroups = collaboratedGroups.filter(
    (g) => g && Array.isArray(g.products) && g.products.length > 0
  );

  if (validCollabGroups.length === 0) {
    return mainOptions;
  }

  const collabGroupOptions = validCollabGroups.map((group) => {
    const compCode = group.companyCode || "PARTNER";
    const compName = group.companyName || compCode;
    const companyKey = `comp:${compCode}`;

    const childOptions = buildProductCascaderOptions(group.products).map((opt) => {
      const optionVal = `${companyKey}:${opt.value}`;
      return {
        ...opt,
        partnerCompanyCode: compCode,
        partnerCompanyName: compName,
        value: optionVal,
        displayName: `${opt.name} (${compName})`,
        children: opt.children?.map((subOpt) => ({
          ...subOpt,
          partnerCompanyCode: compCode,
          partnerCompanyName: compName,
          value: `${companyKey}:${subOpt.value}`,
          displayName: `${subOpt.name} (${compName})`,
        })),
      };
    });

    return {
      value: companyKey,
      label: `${compName} (Collaborated)`,
      title: `${compName} (Collaborated Company Products)`,
      partnerCompanyCode: compCode,
      partnerCompanyName: compName,
      isCompanyHeader: true,
      children: childOptions,
    };
  });

  return [...mainOptions, ...collabGroupOptions];
}

/**
 * Filter function for Cascader search
 */
export function cascaderSearchFilter(inputValue, path) {
  const q = String(inputValue || "")
    .trim()
    .toLowerCase();
  if (!q) return false;
  return path.some(
    (option) =>
      String(option.label || "")
        .toLowerCase()
        .includes(q) ||
      String(option.name || "")
        .toLowerCase()
        .includes(q) ||
      String(option.displayName || "")
        .toLowerCase()
        .includes(q) ||
      String(option.partnerCompanyName || "")
        .toLowerCase()
        .includes(q),
  );
}
